/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { audioContext } from './utils';
import AudioRecordingWorklet from './worklets/audio-processing';

import { createWorketFromSrc } from './audioworklet-registry';
import EventEmitter from 'eventemitter3';
import {
  AudioAnalysisTracker,
  configureVoiceAudioAnalyser,
} from '../voice/media/audio-analysis';

interface AudioRecorderOptions {
  sampleRateHz: number;
  onData?: (dataBase64: string) => void;
  onVolume?: (level: number) => void;
  onSpectrum?: (spectrum: number[]) => void;
}

function arrayBufferToBase64(buffer: ArrayBuffer) {
  var binary = '';
  var bytes = new Uint8Array(buffer);
  var len = bytes.byteLength;
  for (var i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

export class AudioRecorder extends EventEmitter {
  stream: MediaStream | undefined;
  audioContext: AudioContext | undefined;
  source: AudioNode | undefined;
  recording: boolean = false;
  recordingWorklet: AudioWorkletNode | undefined;
  analyser: AnalyserNode | undefined;
  meterSink: GainNode | undefined;

  private starting: Promise<void> | null = null;
  private animationFrame: number | null = null;
  private readonly analysisTracker = new AudioAnalysisTracker();

  public readonly sampleRate: number;
  private readonly onData?: (dataBase64: string) => void;
  private readonly onVolume?: (level: number) => void;
  private readonly onSpectrum?: (spectrum: number[]) => void;

  constructor(options: number | AudioRecorderOptions = 16000) {
    super();
    this.sampleRate =
      typeof options === 'number' ? options : options.sampleRateHz;
    this.onData = typeof options === 'number' ? undefined : options.onData;
    this.onVolume = typeof options === 'number' ? undefined : options.onVolume;
    this.onSpectrum =
      typeof options === 'number' ? undefined : options.onSpectrum;
  }

  async start(deviceId?: string): Promise<void> {
    console.log('AudioRecorder.start() called');
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      console.error('MediaDevices API not available');
      throw new Error('Could not request user media');
    }

    this.starting = new Promise(async (resolve, reject) => {
      try {
        console.log(
          'Requesting microphone access with specific constraints...',
        );
        this.stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            channelCount: 1,
            sampleRate: this.sampleRate,
          },
        });

        // Check if we actually got audio tracks
        const audioTracks = this.stream.getAudioTracks();
        console.log('Microphone access granted, tracks:', audioTracks.length);
        if (audioTracks.length > 0) {
          const track = audioTracks[0];
          console.log('Audio track settings:', track.getSettings());
          console.log('Audio track constraints:', track.getConstraints());
          console.log('Audio track capabilities:', track.getCapabilities());
        }

        console.log('Creating audio context...');
        this.audioContext = await audioContext({ sampleRate: this.sampleRate });
        console.log('Audio context created, sample rate:', this.sampleRate);

        console.log('Creating media stream source...');

        // Create a media stream source from the microphone
        this.source = this.audioContext.createMediaStreamSource(this.stream);
        console.log('Media stream source created');

        this.analyser = this.audioContext.createAnalyser();
        configureVoiceAudioAnalyser(this.analyser);
        this.meterSink = this.audioContext.createGain();
        this.meterSink.gain.setValueAtTime(0, this.audioContext.currentTime);
        this.source.connect(this.analyser);
        this.analyser.connect(this.meterSink);
        this.meterSink.connect(this.audioContext.destination);

        // The worklet name must match the class name in the worklet
        const workletName = 'audio-recorder-worklet';
        console.log('Creating audio recording worklet...');
        const src = createWorketFromSrc(workletName, AudioRecordingWorklet);

        console.log('Adding audio worklet module...');
        await this.audioContext.audioWorklet.addModule(src);
        console.log('Audio worklet module added');

        console.log('Creating AudioWorkletNode...');
        this.recordingWorklet = new AudioWorkletNode(
          this.audioContext,
          workletName,
        );
        console.log('AudioWorkletNode created');

        this.recordingWorklet.port.onmessage = async (ev: MessageEvent) => {
          // worklet processes recording floats and messages converted buffer
          const arrayBuffer = ev.data.data.int16arrayBuffer;

          if (arrayBuffer) {
            const arrayBufferString = arrayBufferToBase64(arrayBuffer);
            this.onData?.(arrayBufferString);
            this.emit('data', arrayBufferString);
          }
        };

        console.log('Connecting source to recording worklet...');
        this.source.connect(this.recordingWorklet);
        console.log('Source connected to recording worklet');

        this.recording = true;
        this.sampleSpectrum();
        console.log('Audio recorder started successfully');
        resolve();
      } catch (error) {
        console.error('Error starting audio recorder:', error);
        reject(error);
      } finally {
        this.starting = null;
      }
    });

    return this.starting;
  }

  async stop(): Promise<void> {
    // its plausible that stop would be called before start completes
    // such as if the websocket immediately hangs up
    const handleStop = async () => {
      if (this.recordingWorklet) this.recordingWorklet.port.onmessage = null;
      this.stopSpectrumSampling();
      this.source?.disconnect();
      this.analyser?.disconnect();
      this.meterSink?.disconnect();
      this.stream?.getTracks().forEach((track) => track.stop());
      const context = this.audioContext;
      this.stream = undefined;
      this.source = undefined;
      this.audioContext = undefined;
      this.recordingWorklet = undefined;
      this.analyser = undefined;
      this.meterSink = undefined;
      this.recording = false;
      if (context && context.state !== 'closed') await context.close();
    };
    if (this.starting) {
      await this.starting.catch(() => undefined);
      await handleStop();
      return;
    }
    await handleStop();
  }

  private sampleSpectrum = (): void => {
    const analyser = this.analyser;
    if (!this.recording || !analyser) return;
    const frequencyBytes = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteFrequencyData(frequencyBytes);
    const frame = this.analysisTracker.sample(
      frequencyBytes,
      performance.now(),
    );
    this.onSpectrum?.(frame.spectrum);
    this.onVolume?.(frame.level);
    this.emit('spectrum', frame.spectrum);
    this.emit('volume', frame.level);
    this.animationFrame = window.requestAnimationFrame(this.sampleSpectrum);
  };

  private stopSpectrumSampling(): void {
    if (this.animationFrame !== null) {
      window.cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
    const frame = this.analysisTracker.reset();
    this.onSpectrum?.(frame.spectrum);
    this.onVolume?.(frame.level);
    this.emit('spectrum', frame.spectrum);
    this.emit('volume', frame.level);
  }
}
